import { createContext, useCallback, useContext, useMemo } from 'react';
import type { FunctionComponent } from 'react';

import { useAddToast } from '../../../../../contexts/ToastContext';
import { useEditable } from '../../../../../hooks';
import useUpdateReleaseGroup from '../hooks/useUpdateReleaseGroup';
import type { ProjectGroup } from '../../../../../../shared/model-typings';
import type { UpdateProjectGroupRequest } from '../../../../../../typings/api';
import type { UseEditable } from '../../../../../hooks/useEditable';

type ReleaseGroupSettingsFields =
  | 'title'
  | 'policyId'
  | 'securityPolicyId'
  | 'qualityPolicyId'
  | 'publicOnPortal';

export type ReleaseGroupSettings = Pick<ProjectGroup, ReleaseGroupSettingsFields>;

type ReleaseGroupSettingsProps = ReleaseGroupSettings & {
  projectGroupId: string; // String as it's coming from the query params
  onRefetchAfterSave: () => Promise<void>;
};

//  This contains the state and handlers for the settings
type ReleaseGroupSettingsState = UseEditable<ReleaseGroupSettings> & Pick<ReleaseGroupSettingsProps, 'onRefetchAfterSave'>;

const ReleaseGroupSettingsContext = createContext<ReleaseGroupSettingsState>({} as ReleaseGroupSettingsState);

export const useReleaseGroupsSettings = () => useContext(ReleaseGroupSettingsContext);

/**
 * This component reads the projectGroup which is passed to it from the parent component,
 * and sets up the state for the settings to be edited and saved.
 */
const ReleaseGroupSettingsContextProvider: FunctionComponent<ReleaseGroupSettingsProps> = ({
  children,
  projectGroupId,
  title,
  policyId,
  securityPolicyId,
  qualityPolicyId,
  publicOnPortal,
  onRefetchAfterSave,
}) => {
  const onAddToast = useAddToast();
  const onUpdateReleaseGroup = useUpdateReleaseGroup();

  //  We need to memoize this so that useEditable can determine if the settings have actually changed.
  const settings = useMemo(() => ({
    title,
    policyId,
    securityPolicyId,
    qualityPolicyId,
    publicOnPortal,
  }), [title, policyId, securityPolicyId, qualityPolicyId, publicOnPortal]);
  const { onSave: onSaveSettings, ...state } = useEditable<ReleaseGroupSettings>(settings);

  const onSave = useCallback(async () => {
    try {
      const request: UpdateProjectGroupRequest = {
        title: state.data.title,
        licensingPolicyId: state.data.policyId, // Note this is named different in the request for whatever reason.
        securityPolicyId: state.data.securityPolicyId,
        qualityPolicyId: state.data.qualityPolicyId,
      };
      await onUpdateReleaseGroup(projectGroupId, request);

      //  We need to update Redux, so this will trigger a refetch of the project group
      //  which passes down the settings to this component. This isn't the most efficient,
      //  but it's the easiest way to keep the settings in sync.
      await onRefetchAfterSave();

      onSaveSettings();
      onAddToast({
        type: 'success',
        content: 'Settings saved successfully',
      });
    } catch (error) {
      onAddToast({
        type: 'error',
        content: 'Error saving settings',
      });
    }
  }, [onAddToast, onRefetchAfterSave, onSaveSettings, onUpdateReleaseGroup, projectGroupId, state.data]);

  const contextValue = useMemo(() => ({ ...state, onSave, onRefetchAfterSave }), [state, onSave, onRefetchAfterSave]);

  return (
    <ReleaseGroupSettingsContext.Provider value={contextValue}>
      {children}
    </ReleaseGroupSettingsContext.Provider>
  );
};

export default ReleaseGroupSettingsContextProvider;
